'use client';

import { useState, useRef } from 'react';
import NextImage from 'next/image';
import { HowItWorks } from '@/components/HowItWorks';
import { FAQ } from '@/components/FAQ';
import { RecommendedArticles } from '@/components/RecommendedArticles';
import { Footer } from '@/components/Footer';
import { ResultCard } from '@/components/ResultCard';
import { UserTestimonials } from '@/components/UserTestimonials';
import { AttractivenessRecommendations } from '@/components/AttractivenessRecommendations';
import { FacialScienceVideo } from '@/components/FacialScienceVideo';

import { WebApplicationSchema, ServiceSchema, HowToSchema, FAQSchema } from '@/components/StructuredData';
import GradientButton from '@/components/GradientButton';
import UserStories from '@/components/UserStories';

interface Result {
  score?: number;
  details?: string;
  tips?: string;
  age?: number;
  gender?: string;
  emotion?: string;
  imageUrl?: string;
}

export default function Home() {
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [lastUploadTime, setLastUploadTime] = useState(0);
  const MIN_UPLOAD_INTERVAL = 2000;
  const resultCardRef = useRef<HTMLDivElement>(null);
  const isUploadingRef = useRef(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImage(file);
    setError('');
    setResult(null);
    setLoading(true);

    try {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setError('Failed to process image');
          setLoading(false);
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const jpegBase64 = canvas.toDataURL('image/jpeg', 0.9);
        
        try {
          const response = await fetch('/api/score', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ image: jpegBase64 }),
          });

          const data = await response.json();
          
          if (data.error) {
            setError(data.error);
            setPreviewUrl(jpegBase64);
          } else {
            setResult({
              ...data,
              imageUrl: jpegBase64,
            });
            setPreviewUrl('');
          }
        } catch (err) {
          setError('Failed to analyze image. Please try again.');
          setPreviewUrl(jpegBase64);
        } finally {
          setLoading(false);
        }
      };

      img.onerror = () => {
        setError('Failed to load image');
        setLoading(false);
      };

      const reader = new FileReader();
      reader.onloadend = () => {
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to process image');
      setLoading(false);
    }
  };



  return (
    <>
      {/* Structured Data */}
      <WebApplicationSchema 
        name="AI Attractiveness Test - Most Accurate Free Beauty Score Online"
        description="Get the most accurate AI attractiveness test online. Free facial beauty analysis with instant AI scoring."
        url="https://www.aiattractiveness.com"
      />
      <ServiceSchema
        name="AI Attractiveness Testing Service"
        description="Professional AI-powered beauty and attractiveness analysis service"
        url="https://www.aiattractiveness.com"
      />
      <HowToSchema
        name="How to Use AI Attractiveness Test"
        description="Step-by-step guide to using our AI attractiveness test for accurate beauty scoring"
        steps={[
          {
            name: "Upload Your Photo",
            text: "Select a clear, front-facing photo of yourself with good lighting"
          },
          {
            name: "AI Analysis",
            text: "Our advanced AI analyzes your facial features, symmetry, and proportions"
          },
          {
            name: "Get Your Score",
            text: "Receive your attractiveness score with detailed analysis and improvement tips"
          }
        ]}
      />
      
      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-purple-50">
          <div className="container mx-auto max-w-7xl px-6 py-16 md:py-20">

            {/* Main Title Section */}
            <div className="text-center mb-12">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-800 leading-tight">
                AI Attractiveness Test
              </h1>
              <p className="mt-4 text-xl text-gray-600">
                Get Your Beauty Score Instantly
              </p>
            </div>

            <div className="grid lg:grid-cols-12 gap-16 items-center">

              {/* Left Content */}
              <div className="lg:col-span-5 space-y-2 lg:space-y-8 text-center lg:text-left">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
                  Test Your Attractiveness
                </h2>

                {/* Image - 在移动端显示在标题下面 */}
                <div className="lg:hidden flex justify-center mt-3 mb-2">
                  <NextImage
                    src="/images/background.jpg"
                    alt="AI Attractiveness Test Visualization"
                    width={320}
                    height={320}
                    className="rounded-lg object-cover shadow-2xl"
                    priority
                  />
                </div>

                <p className="text-lg text-gray-600 leading-relaxed">
                  Let our AI judge your attractiveness. Want to know how attractive you are in other people's eyes? Upload a photo of your face now and find out.
                </p>
                <div className="pt-4">
                    <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} accept="image/*" disabled={loading} />
                    <button
                      type="button"
                      onClick={() => document.getElementById('file-upload')?.click()}
                      disabled={loading}
                      className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg font-semibold hover:from-blue-700 hover:to-purple-700 cursor-pointer transition-all duration-300 transform hover:-translate-y-1 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-75 disabled:cursor-not-allowed w-full max-w-xs mx-auto md:w-auto md:max-w-none"
                  >
                    {loading ? (
                         <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                           Analyzing...
                         </>
                    ) : (
                        'Upload your image'
                    )}
                    </button>
                    <p className="mt-4 text-sm text-gray-500 flex items-center justify-center lg:justify-start">
                        <svg className="w-4 h-4 mr-2 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Photos are processed server-side. We don't save or share them.
                    </p>
                </div>
              </div>

              {/* Right Content - Image (桌面端显示) */}
              <div className="hidden lg:flex lg:col-span-7 justify-center lg:justify-end">
                <NextImage
                  src="/images/background.jpg"
                  alt="AI Attractiveness Test Visualization"
                  width={700}
                  height={700}
                  className="rounded-lg object-cover shadow-2xl"
                  priority
                />
              </div>

            </div>
          </div>
        </section>

        {/* Upload Section */}
        {(previewUrl || result) && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  {/* Preview image only shown when there's no result */}
                  {previewUrl && !result && (
                    <div className="text-center">
                      <div className="relative w-64 h-64 mx-auto rounded-xl overflow-hidden mb-6">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {error && (
                        <div className="text-red-600 text-center">{error}</div>
                      )}
                    </div>
                  )}

                  {/* Show result card with the image */}
                  {result && (
                    <div>
                      <ResultCard
                        result={result}
                        imageUrl={result.imageUrl}
                        onAnalyzeAnother={() => {
                          setResult(null);
                          setPreviewUrl(null);
                          setError(null);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 7 indicators Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="container mx-auto max-w-7xl px-6">
            
            {/* Section Title */}
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
                AI Attractiveness Test — Our Test Results
              </h2>
              </div>
              
            <div className="flex flex-col md:flex-row gap-16 items-center">
              {/* Left - Content */}
              <div className="order-2 md:order-1 flex-1 space-y-6">
                <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                  7 indicators of AI attractiveness test results
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  More than just attractiveness. Our AI attractiveness test provides 7 comprehensive indicators to evaluate your face. They will give a comprehensive picture of your appearance, potential personality, and confidence level.
                </p>
                <ul className="list-disc pl-6 text-base text-gray-700 space-y-1">
                  <li>Overall attractiveness score</li>
                  <li>Smile index</li>
                  <li>Opposite sex attractiveness</li>
                  <li>Skin texture</li>
                  <li>Age</li>
                  <li>Gender</li>
                  <li>Mood</li>
                </ul>
                <div className="pt-4 flex justify-center md:justify-start">
                  <GradientButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Try it now
                  </GradientButton>
                </div>
              </div>
              
              {/* Right - Image */}
              <div className="order-1 md:order-2 flex-1 flex justify-center">
                <NextImage
                  src="/images/result.png"
                  alt="AI Attractiveness Test Result Indicators"
                  width={700}
                  height={700}
                  className="rounded-lg object-cover shadow-2xl"
                  priority
                />
              </div>
            </div>
          </div>
        </section>

        {/* What can AI Attractiveness Test do? Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="container mx-auto max-w-7xl px-6">
            
            {/* Section Title */}
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
                What can AI Attractiveness Test do?
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Our powerful AI attractiveness test can be widely used in your daily life and entertainment.
              </p>
            </div>

            {/* Sub-section 1: Social Media */}
            <div className="flex flex-col md:flex-row gap-16 items-center mb-24">
              <div className="order-1 md:order-1 flex-1 flex justify-center">
                <NextImage
                  src="/images/social-media-dominance.jpg"
                  alt="Social Media Dominance"
                  width={630}
                  height={360}
                  className="rounded-lg object-cover shadow-2xl"
                />
              </div>
              <div className="order-2 md:order-2 flex-1 space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">Social Media Dominance</h3>
                <p className="text-gray-600">
                  Unleash the power of the AI attractiveness test that's breaking the internet—our revolutionary free attractiveness test delivers instant 1-10 beauty scores that social media can't get enough of. This accurate AI attractiveness test features advanced facial attractiveness analysis, golden ratio scoring, and beauty rating technology that turns every selfie into viral content. Content creators are obsessing over our AI beauty test app because it's the most reliable attractiveness test online—producing instant beauty analysis that beats any traditional beauty assessment tool. Watch your engagement explode when you share results from the best AI attractiveness test that Reddit users call "brutally accurate" and TikTok creators tag as #ultimate-beauty-test.
                </p>
                <div className="pt-2 flex justify-center md:justify-start">
                  <GradientButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Try it now
                  </GradientButton>
                </div>
              </div>
            </div>

            {/* Sub-section 2: Dating Profile */}
            <div className="flex flex-col md:flex-row gap-16 items-center mb-24">
              <div className="order-1 md:order-2 flex-1 flex justify-center">
                <NextImage 
                  src="/images/dating-profile-optimization.jpg"
                  alt="Dating Profile Optimization"
                  width={630}
                  height={360}
                  className="rounded-lg object-cover shadow-2xl"
                />
              </div>
              <div className="order-2 md:order-1 flex-1 space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">Dating Profile Optimization</h3>
                <p className="text-gray-600">
                  Transform your dating success with our comprehensive AI attractiveness analysis that goes beyond basic face rating apps—our intelligent beauty test evaluates facial symmetry, body proportions, and visual appeal using cutting-edge AI facial analysis technology. This professional attractiveness assessment provides personalized beauty insights calibrated for diverse preferences, delivering scientific beauty ratings more accurate than any online attractiveness calculator. Users report dramatic improvements in match rates after optimizing profiles with our AI-powered beauty evaluation—because our advanced attractiveness test combines machine learning beauty analysis with real-time attractiveness scoring to give you the competitive edge in digital dating.
                </p>
                <div className="pt-2 flex justify-center md:justify-start">
                  <GradientButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Try it now
                  </GradientButton>
                </div>
                </div>
              </div>
              
            {/* Sub-section 3: Personal Development */}
            <div className="flex flex-col md:flex-row gap-16 items-center">
              <div className="order-1 md:order-1 flex-1 flex justify-center">
                <NextImage
                  src="/images/personal-development-self-discovery.jpg"
                  alt="Personal Development & Self-Discovery"
                  width={630}
                  height={360}
                  className="rounded-lg object-cover shadow-2xl"
                />
                  </div>
              <div className="order-2 md:order-2 flex-1 space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">Personal Development & Self-Discovery</h3>
                <p className="text-gray-600">
                  Experience the ultimate AI beauty assessment designed for continuous self-improvement—our comprehensive attractiveness analysis tracks your glow-up journey with detailed facial feature evaluation and personalized beauty recommendations. This smart beauty rating system stores your attractiveness score history privately, creating a digital beauty portfolio that celebrates your evolution. Whether you need an honest attractiveness evaluation, want to track beauty progress, or explore facial symmetry analysis, our AI beauty intelligence platform transforms data into actionable confidence-building insights that help you understand and enhance your natural appeal.
                </p>
                <div className="pt-2 flex justify-center md:justify-start">
                  <GradientButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Try it now
                  </GradientButton>
                </div>
              </div>
            </div>

            {/* User Stories Sub-section */}
            <div className="pt-24">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
                  User stories of AI Attractiveness Test
                </h2>
                <p className="mt-4 text-lg text-gray-600">
                  Good product experience comes from the real feelings of users
                </p>
              </div>
              <UserStories />
            </div>

          </div>
        </section>

        {/* FAQ Section */}
        <FAQ
          title="FAQs of AI Attractiveness Test"
          faqs={[
            {
              question: 'What can the AI attractiveness test do?',
              answer: `The  AI attractiveness test is built for fun and self-discovery. Upload a selfie and get an instant 1-10 beauty score plus detailed metrics like smile index and skin status. It’s also the best ai attractiveness test for picking high-scoring photos for your dating profile or social media feed, helping you make a great first impression. Whether you want to explore or level-up your online image, our free ai attractiveness test delivers a unique, engaging experience.`
            },
            {
              question: 'Is the AI attractiveness test private and safe?',
              answer: `Absolutely. Your privacy is 100 % protected. We do not save, store, or share any image you upload for the test.`
            },
            {
              question: 'Is the attractiveness test free?',
              answer: `Yes! Our ai attractiveness test is completely free—no hidden charges or sign-up required.`
            },
            {
              question: 'How can I raise my attractiveness test score?',
              answer: `Follow these quick tips to boost your score on any accurate attractiveness test:<br /><br />
• Smile genuinely—it lifts your smile index.<br />
• Keep clean and fresh; hygiene matters in every attractiveness test.<br />
• Make eye contact with the camera; it strengthens perceived connection.<br />
• Get enough sleep so your skin and eyes look energized.<br />
• Wear well-fitted clothes that highlight your best features and confidence.`
            },
            {
              question: 'What if the test shows no result or an error?',
              answer: `Sorry for the hiccup. Please try another photo first. If the issue persists, wait a few minutes and reload the ai attractiveness test page.`
            },
            {
              question: 'Still have questions?',
              answer: `Reach out via the <a href="/contact" class="text-blue-600 underline hover:text-blue-800">Contact Us</a> page—our support team is ready to help with any other inquiries about the best free attractiveness test online.`
            }
          ]}
        />

        {/* How to Use Our AI Attractiveness Test Step by Step? */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How to Use Our AI Attractiveness Test Step by Step?
              </h2>
            </div>
            <div className="flex flex-col md:flex-row gap-12 max-w-4xl mx-auto items-center">
              {/* 右侧图片（移动端在上） */}
              <div className="order-1 md:order-2 flex-1 flex justify-center mb-8 md:mb-0">
                <NextImage
                  src="/images/upload-image.jpg"
                  alt="How to Use AI Attractiveness Test"
                  width={400}
                  height={400}
                  className="rounded-xl object-cover shadow-2xl"
                />
              </div>
              {/* 左侧步骤 */}
              <div className="order-2 md:order-1 flex-1 space-y-10">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">1</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Step 1: Upload Your Photo</h3>
                    <p className="text-gray-600">Hit the upload button and add a clear, front-facing image. No sign-up needed—this free AI attractiveness test starts the moment your photo arrives.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">2</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Step 2: Get Your Instant Attractiveness Score</h3>
                    <p className="text-gray-600">In seconds the best AI attractiveness test delivers a 1–10 overall beauty score plus detailed metrics like smile index, skin status, and side-profile attractiveness—everything you need to pick your most photogenic shot.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 relative bg-gradient-to-br from-blue-500/90 via-purple-500/80 to-pink-400/80 rounded-3xl mx-4">
          <div className="container mx-auto px-6 text-center relative z-10">
            <h2 className="text-4xl font-bold mb-6 text-white drop-shadow-lg">
              Ready to unlock your AI Attractiveness Test beauty score?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90 text-white/90">
              Join thousands of users who trust our AI attractiveness test for accurate beauty analysis
            </p>
            <GradientButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              Try it now
            </GradientButton>
          </div>
          {/* 渐变光圈装饰 */}
          <div className="absolute left-1/2 top-3/4 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[120px] bg-gradient-to-r from-blue-400/30 via-purple-400/20 to-pink-300/10 rounded-full blur-2xl opacity-70 z-0"></div>
        </section>

        {/* User Stories Section */}
        

        <Footer />
      </main>
    </>
  );
}
